/** @format */

/* eslint require-await: "warn" -- Legacy code: do not use as an example. */
import { MpbLogo } from '@mpb-com/feather/components/atoms/mpb-logo';
import { useTranslation } from 'next-i18next';
import NextLink from 'next/link';
import React, { FunctionComponent, ReactNode, useContext } from 'react';
import styled from 'styled-components';

import { getConfig } from '@toucan/common/services/nextjs/settings';

import { MarketCode, MarketContext } from '@toucan/client/global/market';

import {
    ConstrainedWrapper,
    ConstrainedWrapperGutter,
    ConstrainedWrapperType,
} from '@toucan/client/shared/components/constrained-wrapper';
import { CopyrightNotice } from '@toucan/client/shared/components/copyright-notice';
import {
    PaymentMethods,
    PaymentMethodsColour,
} from '@toucan/client/shared/components/payment-methods';
import { PaypalCreditNotice } from '@toucan/client/shared/components/paypal-credit-notice';
import { TrustedShopsBadge } from '@toucan/client/shared/components/trusted-shops-badge';

import { DesktopLinks } from './components/desktop-links';
import { MarketSelector } from './components/market-selector';
import { MobileLinks } from './components/mobile-links';
import { FooterVariant } from './constants';
import { styles } from './footer.styles';
import { FooterLinks, FooterProps } from './types';

export { FooterVariant };
export type { FooterLinks };

const StyledFooter = styled.footer`
    ${styles}
`;

export const Footer: FunctionComponent<FooterProps> = ({
    linkLists,
    variant,
    'data-testid': dataTestId,
    'data-id': dataId,
    className,
}: FooterProps) => {
    const {
        publicRuntimeConfig: { trustedShopsId, marketsWithTrustedShops },
    } = getConfig();
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);

    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    const displayTrustedShopsBadge =
        variant === FooterVariant.DESKTOP &&
        marketsWithTrustedShops?.includes(activeMarket.code);

    function getLinksElement(): ReactNode {
        switch (variant) {
            case FooterVariant.MOBILE:
                return (
                    <MobileLinks
                        links={linkLists}
                        data-id="footer__mobile-links"
                    />
                );
            case FooterVariant.DESKTOP:
            default:
                return (
                    <DesktopLinks
                        links={linkLists}
                        data-id="footer__desktop-links"
                    />
                );
        }
    }

    return (
        <StyledFooter
            data-id={dataId}
            data-testid={dataTestId}
            className={`${className} footer--${variant}`}
        >
            <ConstrainedWrapper
                className="content-wrapper"
                type={ConstrainedWrapperType.PADDING}
                gutter={ConstrainedWrapperGutter.SMALL_GUTTER}
            >
                <div className="top-row" data-id="footer__top-row">
                    <div className="logos" data-id="footer__logos">
                        <NextLink
                            prefetch={false}
                            href="/"
                            as={`/${activeMarket.slug}`}
                            legacyBehavior
                        >
                            <MpbLogo
                                displayStrapline
                                href="/"
                                data-testid="footer__logo"
                                data-id="footer__logo"
                                className="mpb-logo"
                            />
                        </NextLink>

                        {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                        {displayTrustedShopsBadge && (
                            <TrustedShopsBadge
                                trustedShopsId={trustedShopsId}
                                id="trustedShopsBadge"
                                className="trusted-shops-badge"
                                data-testid="footer__trusted-shops-badge"
                                data-id="footer__trusted-shops-badge"
                            />
                        )}
                    </div>

                    <nav
                        className="links"
                        data-testid="footer__links"
                        data-id="footer__links"
                    >
                        {getLinksElement()}
                    </nav>
                    {activeMarket.code === MarketCode.EN_UK && (
                        <PaypalCreditNotice
                            className="credit-message"
                            data-testid="footer__credit-message"
                        />
                    )}
                </div>
                <div className="bottom-row" data-id="footer__bottom-row">
                    <MarketSelector
                        className="market-selector"
                        data-testid="footer__market-selector"
                        data-id="footer__market-selector"
                    />
                    <PaymentMethods
                        market={activeMarket.code}
                        colour={PaymentMethodsColour.MIDNIGHT}
                        className="payment-methods"
                        data-testid="footer__payment-methods"
                        data-id="footer__payment-methods"
                    />
                    <CopyrightNotice
                        year={new Date().getFullYear()}
                        copyrightHolder={t('MPB', { context: 'copyright' })}
                        className="copyright-notice"
                        data-testid="footer__copyright-notice"
                        data-id="footer__copyright-notice"
                    />
                </div>
            </ConstrainedWrapper>
        </StyledFooter>
    );
};
